﻿import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Table } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import { FETCH_VARIETIES_SUCCESS, FINAL_VARIETIES_REQUEST, INITIAL_VARIETIES_REQUEST } from "@/redux/types/maintainers/varieties.type";

import classes from "../maintainers.module.scss";

import api from "@/services/api.service";
import { hasPermission, toast } from "@/services/Utils";

import Head from "@/components/Content/Head";
import BasePagination from "@/components/Pagination/BasePagination";
import Fetching from "@/components/Fetching/Fetching";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import VarietiesTableHead from "./VarietiesTableHead";
import VarietiesTableBody from "./VarietiesTableBody";
import VarietiesForm from "./VarietiesForm/VarietiesForm";
import { filters } from "./varieties.constants";
import VarietiesFilter from "./VarietiesFilter";
import ButtonFilter from "@/components/Filter/ButtonFilter";

const Varieties = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const { addToast } = useToasts();
	const navigate = useNavigate();

	const { search, estate_id } = useSelector((state) => state.auth.filter);
	const { isFetching, quantity, last_page, from, to, customers, categories, species } = useSelector((state) => state.maintainers_varieties);

	const [selected, setSelected] = useState("");
	const [paginate, setPaginate] = useState(20);
	const [page, setPage] = useState(1);
	const [order, setOrder] = useState("specie_name");
	const [orderType, setOrderType] = useState("asc");
	const [loaded, setLoaded] = useState(false);
	const [filter, setFilter] = useState({ ...filters });
	const [showFilter, setShowFilter] = useState(false);
	const [permissions, setPermissions] = useState([]);

	const [showDelete, setShowDelete] = useState(false);

	useEffect(() => {
		dispatch({ type: INITIAL_VARIETIES_REQUEST });
		api.get("/admin/varieties", {
			page,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			...filter,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			updateData(response);
		});
		// eslint-disable-next-line
	}, [search, estate_id, order, orderType, paginate, page, filter]);

	const updateData = (response) => {
		const { varieties, customers, categories, species, estates, permissions: allPermissions } = response;
		dispatch({
			type: FETCH_VARIETIES_SUCCESS,
			payload: {
				varieties: varieties.data,
				last_page: varieties.last_page,
				from: varieties.from,
				to: varieties.to,
				quantity: varieties.total,
				customers: customers,
				species: species,
				categories: categories,
				estates: estates,
			},
		});
		setLoaded(true);
		setPermissions(allPermissions);
	};

	const handleDelete = (item) => {
		setSelected(item.id);
		setShowDelete(true);
	};

	const confirmDelete = () => {
		setShowDelete(false);
		setPage(1);

		dispatch({ type: INITIAL_VARIETIES_REQUEST });
		api.post("/admin/varieties/delete", {
			page,
			selected,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			...filter,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			setSelected("");
			if (Object.prototype.hasOwnProperty.call(response, "varieties")) {
				toast(addToast, "Variedad eliminado correctamente", "success");
				updateData(response);
			} else {
				dispatch({ type: FINAL_VARIETIES_REQUEST });
			}
		});
	};

	const confirmSave = (form, modalFetching, afterSave) => {
		modalFetching(true);
		setPage(1);

		api.post("/admin/varieties/save", {
			...form,
			page,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			...filter,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			updateData(response);
			modalFetching(false);
			afterSave();
		});
	};

	const applyFilter = (params) => {
		let new_filter = { ...filter };

		if (params.customerFilter !== filter.filter_customer) {
			new_filter = {
				...new_filter,
				filter_customer: params.customerFilter,
			};
		}
		if (params.estateFilter !== filter.filter_estate) {
			new_filter = { ...new_filter, filter_estate: params.estateFilter };
		}

		if (params.categoryFilter !== filter.filter_category) {
			new_filter = {
				...new_filter,
				filter_category: params.categoryFilter,
			};
		}

		if (params.specieFilter !== filter.filter_specie) {
			new_filter = { ...new_filter, filter_specie: params.specieFilter };
		}

		setFilter(new_filter);
		setShowFilter(false);
	};

	if (isFetching) {
		return <Fetching />;
	}

	return (
		<>
			<Head
				title="Paso 2"
				button={hasPermission("create", permissions) ? true : false}
				button_text={"Nuevo Registro"}
				action={() => navigate("/configuracion/paso-2/crear")}
				button_filter={true}
				back_url={"/configuracion?section=businessCenter"}
			/>

			<ButtonFilter origin="main" size_md={12} showFilter={showFilter} setShowFilter={setShowFilter} />

			{hasPermission("show", permissions) && (
				<Row className={` ${classes.main_row}`}>
					{showFilter && <VarietiesFilter filter={filter} applyFilter={applyFilter} />}

					<Col md={showFilter ? 9 : 12} className={classes.main_col}>
						<Row className={classes.header}>
							<ButtonFilter origin="actions" size_md={1} showFilter={showFilter} setShowFilter={setShowFilter} />
						</Row>

						<Row className={classes.table}>
							<div className={classes.table_div}>
								<Table className={classes.table_desktop}>
									<VarietiesTableHead order={order} setOrder={setOrder} orderType={orderType} setOrderType={setOrderType} />
									<VarietiesTableBody handleDelete={handleDelete} permissions={permissions} />
								</Table>
							</div>
						</Row>

						<BasePagination
							page={page}
							paginate={paginate}
							last_page={last_page}
							quantity={quantity}
							from={from}
							to={to}
							setPage={setPage}
							setPaginate={setPaginate}
						/>
					</Col>
				</Row>
			)}

			{loaded && (
				<>
					<BasicConfirm showConfirm={showDelete} setShowConfirm={setShowDelete} handleAction={confirmDelete} />
					<VarietiesForm customers={customers} categories={categories} species={species} confirmSave={confirmSave} permissions={permissions} />
				</>
			)}
		</>
	);
};

export default Varieties;
